/**
 * The layers tree: the document's structure as selectable nodes.
 *
 * It exists because the canvas alone is not a complete editing surface. A block
 * can be visually empty (a section with nothing in it), fully covered by a child,
 * or hidden behind a `{% if %}` that the current sample data resolves to false —
 * all unclickable on the canvas, all reachable here. In Sprint 2.4 it doubles as
 * a second, always-accurate drop target for deep nesting.
 *
 * Slot nodes appear only for multi-slot containers. For a single-slot container
 * (`core.section`) an intermediate "Body" node would add a level of nesting that
 * carries no information; for `core.columns` the col1/col2/col3 distinction is
 * exactly what the user is navigating.
 */
import { Block, EmailDocument } from './document';
import { resolveBlockDisplayLabel, resolveSlotLabel } from './labels';
import { Registry } from './registry';
import { slotsOf } from './tree';

export interface LayerNode {
  /** Block id, or `<blockId>:<slotIndex>` for a slot node. */
  key: string;
  title: string;
  /**
   * Both kinds of node are pickable, but they mean different things: a block node
   * selects that block, a slot node aims the next insert at that column (and
   * selects its container). `blockId` is what tells them apart — a slot node has
   * none.
   */
  selectable: boolean;
  /** Set on block nodes only. */
  blockId?: string;
  /** Set on block nodes: the block carries a visibility condition. */
  conditional?: boolean;
  /**
   * Set on a slot node whose component does not currently render it, yet which
   * holds blocks — so that content is being **silently discarded**. Only shown for
   * non-empty slots: an unrendered empty slot is simply omitted.
   */
  orphaned?: boolean;
  children?: LayerNode[];
}

/** `<blockId>:<slotIndex>` — the same shape the canvas' slot markers use. */
export function slotNodeKey(blockId: string, slot: number): string {
  return `${blockId}:${slot}`;
}

export interface LayersContext {
  registry: Registry;
  labels?: Record<string, string>;
  /** Title for a block whose type is not in the registry at all. */
  unknownLabel: string;
  /**
   * `<blockId>:<slotIndex>` for the slots the canvas actually rendered.
   *
   * **A component may declare more slots than it renders**, conditionally on its
   * own props: `core/columns@1` declares `col1..col3` but wraps `{% block col3 %}`
   * in `{% if column_count == 3 %}`, so with the default `two` layout that block
   * does not exist and Twig discards anything compiled into it — verified in the
   * browser, where a block placed in the third column left no marker in the frame
   * at all.
   *
   * No manifest can express that today, and preflight cannot evaluate a Twig
   * conditional. The rendered markers are the one generic source of truth, and
   * they cost nothing extra: the canvas measures them already. Undefined (before
   * the first measurement) means "assume every declared slot is usable".
   */
  renderedSlots?: ReadonlySet<string>;
}

function titleFor(block: Block, context: LayersContext): string {
  /**
   * The raw-HTML primitive is named from the label catalog like everything else. It
   * used to carry its own frontend string, which meant the tree said "Custom HTML"
   * while the properties tab and the selection readout said "Eigenes HTML" — the same
   * two-names-for-one-block bug that the partner partials had.
   */
  const definition = context.registry.definitions[block.type];
  if (!definition) return context.unknownLabel;
  return resolveBlockDisplayLabel(definition, context.labels);
}

function nodeFor(block: Block, context: LayersContext): LayerNode {
  const node: LayerNode = {
    key: block.id,
    blockId: block.id,
    title: titleFor(block, context),
    selectable: true,
  };
  if (block.visibility) node.conditional = true;

  const definition = context.registry.definitions[block.type];
  const slots = block.children ?? [];
  if (slots.length === 0) return node;

  if (slots.length === 1) {
    const children = slots[0].map((child) => nodeFor(child, context));
    if (children.length > 0) node.children = children;
    return node;
  }

  const slotNodes: LayerNode[] = [];
  slots.forEach((children, index) => {
    const key = slotNodeKey(block.id, index);
    const rendered = context.renderedSlots?.has(key) ?? true;

    // An unrendered *empty* slot is not a place anything can go: offering it would
    // let a user drop content that Twig then throws away.
    if (!rendered && children.length === 0) return;

    const manifestSlot = definition?.slots[index];
    const slotNode: LayerNode = {
      key,
      title: manifestSlot
        ? resolveSlotLabel(manifestSlot, context.labels)
        : String(index + 1),
      // Selectable so it can be aimed at: without this, columns 2 and 3 of a
      // `core.columns` had no insertion path at all — click-insert always used
      // slot 0, and an empty column has no canvas geometry to drop into.
      selectable: true,
    };
    // Unrendered but occupied: keep it visible and flagged, because hiding it
    // would hide content the user still has to move or delete.
    if (!rendered) slotNode.orphaned = true;
    if (children.length > 0) {
      slotNode.children = children.map((child) => nodeFor(child, context));
    }
    slotNodes.push(slotNode);
  });

  node.children = slotNodes;
  return node;
}

export function layersTreeData(
  document: EmailDocument,
  context: LayersContext,
): LayerNode[] {
  return document.blocks.map((block) => nodeFor(block, context));
}

/** A slot holding content its component does not render, so the content is lost. */
export interface OrphanedSlot {
  blockId: string;
  slot: number;
  /** How many blocks are stranded there. */
  count: number;
}

/**
 * Slots that hold blocks the compiled email will not contain.
 *
 * Drives the editor's warning. It cannot live in `preflight`, which is pure and
 * sees only the compiled body — knowing that `{% block col3 %}` was not reached
 * requires having *rendered* the Twig, which only the canvas does.
 */
export function orphanedSlots(
  blocks: Block[],
  renderedSlots: ReadonlySet<string> | undefined,
  into: OrphanedSlot[] = [],
): OrphanedSlot[] {
  if (!renderedSlots) return into;

  for (const block of blocks) {
    slotsOf(block).forEach((children, slot) => {
      if (children.length === 0) return;
      if (!renderedSlots.has(slotNodeKey(block.id, slot))) {
        into.push({ blockId: block.id, slot, count: children.length });
      }
      orphanedSlots(children, renderedSlots, into);
    });
  }
  return into;
}

/**
 * Every expandable key in the tree.
 *
 * The tree is expanded by default: a collapsed container hides exactly the blocks
 * the canvas can't reach either, which would leave them unreachable altogether.
 */
export function expandableKeys(nodes: LayerNode[], into: string[] = []): string[] {
  for (const node of nodes) {
    if (node.children?.length) {
      into.push(node.key);
      expandableKeys(node.children, into);
    }
  }
  return into;
}
